import { useEffect, useMemo, useState } from "react";
import {
  api,
  type CdnAllowlistDoc,
  type CdnAllowlistInput,
  type CdnDirective,
} from "../../lib/api";

const DIRECTIVE_LABELS: Record<CdnDirective, string> = {
  script_src: "script-src",
  style_src: "style-src",
  font_src: "font-src",
  img_src: "img-src",
  connect_src: "connect-src",
};

const DIRECTIVE_HINTS: Record<CdnDirective, string> = {
  script_src: "External JS bundles loaded via <script src=\"…\">.",
  style_src: "External CSS via <link rel=\"stylesheet\">.",
  font_src: "Web-font hosts referenced from @font-face.",
  img_src: "Image hosts loaded via <img src=\"…\"> or CSS.",
  connect_src: "HTTPS artifact fetch() and EventSource calls beyond same-origin + api.anthropic.com. *.datacamp.com covers subdomains; add datacamp.com separately for the apex. Readable fetch/XHR/EventSource responses must allow this origin with CORS; external WebSockets are not supported.",
};

const DIRECTIVES: CdnDirective[] = ["script_src", "style_src", "font_src", "img_src", "connect_src"];

const HOST_RE = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/;

/** Return whether a directive accepts a bare host or its supported wildcard form. */
export function isValidHostEntry(directive: CdnDirective, host: string): boolean {
  const bareHost = directive === "connect_src" && host.startsWith("*.")
    ? host.slice(2)
    : host;
  return HOST_RE.test(bareHost);
}

function relativeTime(iso: string | undefined): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const seconds = (Date.now() - t) / 1000;
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

/**
 * Admin → CDN allowlist. Edit the per-directive host lists that get
 * appended to the artifact CSP. Changes propagate within ~60s of save
 * (server-side cache TTL).
 */
export function CdnAllowlistPage() {
  const [doc, setDoc] = useState<CdnAllowlistDoc | null>(null);
  const [draft, setDraft] = useState<Record<CdnDirective, string[]> | null>(null);
  const [inputs, setInputs] = useState<Record<CdnDirective, string>>({
    script_src: "",
    style_src: "",
    font_src: "",
    img_src: "",
    connect_src: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"" | "load" | "save">("load");

  useEffect(() => {
    setBusy("load");
    api.admin.cdnAllowlist
      .get()
      .then((d) => {
        setDoc(d);
        setDraft({
          script_src: [...d.script_src],
          style_src: [...d.style_src],
          font_src: [...d.font_src],
          img_src: [...d.img_src],
          connect_src: [...d.connect_src],
        });
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setBusy(""));
  }, []);

  const dirty = useMemo(() => {
    if (!doc || !draft) return false;
    return DIRECTIVES.some(
      (d) => JSON.stringify(doc[d]) !== JSON.stringify(draft[d]),
    );
  }, [doc, draft]);

  function addHost(directive: CdnDirective) {
    const raw = inputs[directive].trim().toLowerCase();
    if (!raw) return;
    if (!isValidHostEntry(directive, raw)) {
      const wildcardHint = directive === "connect_src" ? ' or a leading "*." wildcard' : "";
      setError(
        `Invalid host "${raw}": expected a bare hostname like "cdn.jsdelivr.net"${wildcardHint} (no scheme, path, or port).`,
      );
      return;
    }
    setError(null);
    setDraft((cur) => {
      if (!cur) return cur;
      if (cur[directive].includes(raw)) return cur;
      return { ...cur, [directive]: [...cur[directive], raw] };
    });
    setInputs((cur) => ({ ...cur, [directive]: "" }));
  }

  function removeHost(directive: CdnDirective, host: string) {
    setDraft((cur) =>
      cur ? { ...cur, [directive]: cur[directive].filter((h) => h !== host) } : cur,
    );
  }

  async function save() {
    if (!draft) return;
    setBusy("save");
    setError(null);
    try {
      const payload: CdnAllowlistInput = {
        script_src: draft.script_src,
        style_src: draft.style_src,
        font_src: draft.font_src,
        img_src: draft.img_src,
        connect_src: draft.connect_src,
      };
      const updated = await api.admin.cdnAllowlist.put(payload);
      setDoc(updated);
      setDraft({
        script_src: [...updated.script_src],
        style_src: [...updated.style_src],
        font_src: [...updated.font_src],
        img_src: [...updated.img_src],
        connect_src: [...updated.connect_src],
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy("");
    }
  }

  if (busy === "load" && !doc) return <p className="text-slate-500">Loading…</p>;

  return (
    <div>
      <h1 className="mb-2 text-2xl font-semibold">CDN allowlist</h1>
      <p className="mb-1 text-sm text-slate-500">
        Hostnames added here are appended to the matching CSP directive on every
        artifact serve. Every <code>connect-src</code> host is a place artifact code
        can send viewer data, so add only trusted hosts. CSP is only the browser-side
        gate: targets must use HTTPS and be reachable by the viewer; readable
        cross-origin HTTP responses must also support CORS.
      </p>
      <p className="mb-4 text-xs text-slate-400">
        Last updated {relativeTime(doc?.updated_at)}
        {doc?.updated_by ? ` by ${doc.updated_by}` : ""}. Changes propagate within ~60s.
      </p>

      {error && (
        <p className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}

      {draft && (
        <div className="space-y-4">
          {DIRECTIVES.map((directive) => (
            <div
              key={directive}
              className="rounded-lg border border-slate-200 bg-white p-4"
            >
              <div className="mb-1 flex items-baseline justify-between">
                <h2 className="text-sm font-semibold">
                  <code>{DIRECTIVE_LABELS[directive]}</code>
                </h2>
                <span className="text-xs text-slate-500">
                  {draft[directive].length} host
                  {draft[directive].length === 1 ? "" : "s"}
                </span>
              </div>
              <p className="mb-3 text-xs text-slate-500">
                {DIRECTIVE_HINTS[directive]}
              </p>
              <div className="mb-2 flex flex-wrap gap-2">
                {draft[directive].length === 0 && (
                  <span className="text-xs italic text-slate-400">
                    No hosts allowed.
                  </span>
                )}
                {draft[directive].map((host) => (
                  <span
                    key={host}
                    className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs"
                  >
                    <code className="font-mono">{host}</code>
                    <button
                      type="button"
                      aria-label={`Remove ${host}`}
                      onClick={() => removeHost(directive, host)}
                      className="rounded-full px-1 text-slate-500 hover:bg-slate-200 hover:text-slate-900"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder={directive === "connect_src" ? "*.datacamp.com" : "cdn.example.com"}
                  aria-label={`Add host to ${DIRECTIVE_LABELS[directive]}`}
                  value={inputs[directive]}
                  onChange={(e) =>
                    setInputs((cur) => ({ ...cur, [directive]: e.target.value }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addHost(directive);
                    }
                  }}
                  className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-mono"
                />
                <button
                  type="button"
                  onClick={() => addHost(directive)}
                  className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
                >
                  Add
                </button>
              </div>
            </div>
          ))}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              disabled={!dirty || busy === "save"}
              onClick={() => {
                if (!doc) return;
                setDraft({
                  script_src: [...doc.script_src],
                  style_src: [...doc.style_src],
                  font_src: [...doc.font_src],
                  img_src: [...doc.img_src],
                  connect_src: [...doc.connect_src],
                });
                setError(null);
              }}
              className="rounded-md border border-slate-300 px-4 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!dirty || busy === "save"}
              className="rounded-md bg-slate-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
            >
              {busy === "save" ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
